import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Plus, Minus } from "lucide-react";
import SEO from "../components/SEO";
import Breadcrumbs from "../components/Breadcrumbs";
import CtaBand from "../components/CtaBand";
import { findGroup, groupPath, servicePath } from "../data/services";
import { getHubContent } from "../data/services/content";
import { WHO_DOES_WHAT } from "../data/legal";
import Placeholder from "./Placeholder";

// Standing three-party disclosure rendered on every hub and service page:
// our work, the registered agent's work, the Department's decision.
export function WhoDoesWhat() {
  return (
    <section className="container-page pb-4 pt-2">
      <div className="rounded-2xl bg-acNavySoft/40 p-6">
        <p className="font-semibold text-acblue">{WHO_DOES_WHAT.heading}</p>
        <ul className="mt-3 space-y-2 text-sm text-acBlack/80">
          <li className="flex gap-2"><span className="font-semibold text-acAccent">Us:</span><span>{WHO_DOES_WHAT.us}</span></li>
          <li className="flex gap-2"><span className="font-semibold text-acAccent">Registered agent:</span><span>{WHO_DOES_WHAT.agent}</span></li>
          <li className="flex gap-2"><span className="font-semibold text-acAccent">The Department:</span><span>{WHO_DOES_WHAT.dept}</span></li>
        </ul>
      </div>
    </section>
  );
}

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-acblue/10 py-4">
      <button
        className="flex w-full items-center justify-between gap-4 text-left"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="font-semibold text-acblue">{q}</span>
        {open ? (
          <Minus className="h-5 w-5 shrink-0 text-acAccent" />
        ) : (
          <Plus className="h-5 w-5 shrink-0 text-acAccent" />
        )}
      </button>
      {open && <p className="mt-3 text-sm text-acBlack/70">{a}</p>}
    </div>
  );
}

export default function HubPage({ groupSlug }) {
  const group = findGroup(groupSlug);
  if (!group) return <Placeholder />;

  const hub = getHubContent(groupSlug);
  const heading = hub?.heading || group.title;
  const intro = hub?.intro || group.blurb;

  return (
    <>
      <SEO
        title={heading}
        description={intro}
        path={groupPath(group)}
      />
      <Breadcrumbs
        items={[
          { label: "Home", to: "/" },
          { label: group.title },
        ]}
      />

      {/* Intro */}
      <section className="container-page py-12">
        <p className="eyebrow">{hub?.eyebrow || "Migration support"}</p>
        <h1 className="mt-3 max-w-3xl text-4xl font-bold text-acblue sm:text-5xl">
          {heading}
        </h1>
        <p className="mt-5 max-w-2xl text-lg text-acBlack/70">{intro}</p>
      </section>

      <WhoDoesWhat />


      {/* How it works (hub content only) */}
      {hub?.howItWorks && (
        <section className="bg-acBg py-16">
          <div className="container-page">
            <h2 className="text-2xl font-bold text-acblue sm:text-3xl">
              {hub.howItWorks.heading}
            </h2>
            <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {hub.howItWorks.steps.map((s, i) => (
                <div key={s.title} className="card">
                  <span className="text-2xl font-bold text-acNavySoft">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="mt-2 text-base font-semibold text-acblue">
                    {s.title}
                  </h3>
                  <p className="mt-2 text-sm text-acBlack/70">{s.detail}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Service cards (from registry) */}
      <section className="container-page py-16">
        <h2 className="text-2xl font-bold text-acblue sm:text-3xl">
          Visas we help you prepare for
        </h2>
        <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {group.services.map((s) => (
            <Link
              key={s.slug}
              to={servicePath(group, s)}
              className="card group flex flex-col transition hover:-translate-y-1 hover:shadow-md"
            >
              <h3 className="text-lg font-semibold text-acblue">{s.title}</h3>
              <p className="mt-2 flex-1 text-sm text-acBlack/70">{s.forWho}</p>
              <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-acAccent">
                Learn more
                <ArrowRight className="h-4 w-4 transition group-hover:translate-x-1" />
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* At a glance comparison (hub content only) */}
      {hub?.atAGlance && (
        <section className="bg-acBg py-16">
          <div className="container-page">
            <h2 className="text-2xl font-bold text-acblue sm:text-3xl">
              {hub.atAGlance.heading}
            </h2>
            <div className="mt-8 overflow-x-auto">
              <table className="w-full min-w-[640px] border-separate border-spacing-0 text-left text-sm">
                <thead>
                  <tr>
                    {hub.atAGlance.columns.map((c) => (
                      <th
                        key={c}
                        className="border-b border-acblue/10 bg-white px-4 py-3 font-semibold text-acblue first:rounded-tl-2xl last:rounded-tr-2xl"
                      >
                        {c}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {hub.atAGlance.rows.map((row, ri) => (
                    <tr key={ri} className="bg-white">
                      {row.cells.map((cell, ci) => (
                        <td
                          key={ci}
                          className="border-b border-acblue/10 px-4 py-3 align-top text-acBlack/80"
                        >
                          {ci === 0 && row.serviceSlug ? (
                            <Link
                              to={`/${group.slug}/${row.serviceSlug}/`}
                              className="font-semibold text-acAccent hover:underline"
                            >
                              {cell}
                            </Link>
                          ) : (
                            cell
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}

      <CtaBand />

      {/* FAQs (hub content only) */}
      {hub?.faqs && (
        <section className="container-page max-w-3xl py-16">
          <h2 className="text-2xl font-bold text-acblue sm:text-3xl">
            Common questions
          </h2>
          <div className="mt-8">
            {hub.faqs.map((f) => (
              <FaqItem key={f.q} {...f} />
            ))}
          </div>
        </section>
      )}
    </>
  );
}
